import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { OrgSummary, analysisSettled } from './OrgSummary';
import { OrgHomes, HomesStrip, homeName } from './OrgHomes';
import { OrgPeople } from './OrgPeople';
import { ResolveCouncil } from './ResolveCouncil';
import { AddHomeDialog } from './AddHomeDialog';
import { AddPersonDialog } from './AddPersonDialog';
import { SuggestionRow, FoundBox } from './SuggestionRow';
import { noticedEmptyMessage } from './OrgNoticed';
import { FINDING_CATEGORIES, categoryOf } from '../../lib/findings';
import { buildPeople, glanceTiles, homesDuring, schoolYearsFor, schoolNumbers } from '../../lib/orgProfile';
import { ORG_TYPES_WITH_PLACEMENTS, formatMonthYear } from '../../lib/orgTypes';
import { yearGroupsToDates, UK_YEAR_GROUPS } from '../../lib/schoolYears';
import { getOrgTypeLabel } from '../../lib/utils';

// Brothers and sisters are named in the letter to these (routers/sar.py).
const SIBLING_ORG_TYPES = new Set(['council', 'fostering_agency', 'adoption']);
const SAR_IN_PROGRESS = new Set(['sent', 'awaiting_response', 'partial', 'overdue', 'escalated_ico']);

const Tile = ({ label, value, hint, testId }) => (
  <div className="rounded-lg bg-ground p-5 flex flex-col gap-1" data-testid={testId}>
    <span className="pd-eyebrow">{label}</span>
    <span className="font-heading text-[42px] font-bold tracking-[-0.035em] leading-none text-ink">{value}</span>
    {hint && <span className="text-sm text-ink-55">{hint}</span>}
  </div>
);

// Dates found in the records for this organisation's own (still empty) start/end.
const DatesFound = ({ org, editing }) => {
  const found = (org.pending_suggestions || []).filter((s) => s.type === 'org_dates');
  if (!found.length) return null;
  const ids = found.map((s) => s.id);
  return (
    <FoundBox testId="org-dates-suggestion-banner">
      <SuggestionRow
        disabled={editing.saving}
        title={found.map((s) => `${s.field === 'start_date' ? 'Started' : 'Left'} ${formatMonthYear(String(s.date || '').slice(0, 7)) || s.date}`).join(' · ')}
        detail={`Add ${found.length > 1 ? 'these dates' : 'this date'} to your time with ${org.name}?`}
        actions={[{ label: found.length > 1 ? 'Add dates' : 'Add date', onClick: () => editing.confirmSuggestions(ids), testId: 'confirm-org-dates-btn' }]}
        onIgnore={() => editing.rejectSuggestions(ids)}
      />
    </FoundBox>
  );
};

// "Numbers they used for you" — pupil numbers, case references, each with
// its label. Worth having to hand (exam certificates, a new request), so
// they're on this tab too. Form groups go in the school years; class codes
// fold away.
const Numbers = ({ org, profile, editing, onChange }) => {
  const [showClasses, setShowClasses] = useState(false);
  const found = (org.pending_suggestions || []).filter((s) => s.type === 'reference_numbers');
  const { ids, forms, classes } = schoolNumbers(org, profile);
  const school = org.type === 'school';
  if (!ids.length && !found.length && !(classes.length || (!school && forms.length))) return null;
  return (
    <section aria-labelledby="org-numbers-heading" className="rounded-lg bg-ground p-5 space-y-3" data-testid="org-numbers">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="org-numbers-heading" className="pd-eyebrow">Numbers they used for you</h2>
        <button type="button" onClick={onChange} className="text-sm font-semibold text-brand hover:text-brand-ink">Change</button>
      </div>
      {ids.length > 0 && (
        <dl className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
          {ids.map((n) => (
            <div key={n.value} className="min-w-0">
              <dt className="text-sm text-ink-55">{n.label}</dt>
              <dd className="pd-mono text-ink text-[13px] break-words">{n.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {!school && forms.length > 0 && <p className="text-sm text-ink-70">Groups: <span className="pd-mono text-ink">{forms.map((f) => f.value).join(' · ')}</span></p>}
      {classes.length > 0 && (
        <div className="space-y-1.5">
          <button type="button" onClick={() => setShowClasses((v) => !v)} aria-expanded={showClasses} className="text-sm font-semibold text-brand hover:text-brand-ink" data-testid="org-class-codes">
            {showClasses ? 'Hide' : 'Show'} {classes.length} class code{classes.length === 1 ? '' : 's'}
          </button>
          {showClasses && <p className="pd-mono text-ink text-[12px] leading-relaxed break-words">{classes.map((c) => c.value).join(' · ')}</p>}
        </div>
      )}
      {school && ids.length > 0 && <p className="text-sm text-ink-55">Useful if you ever need copies of your exam certificates.</p>}
      {found.length > 0 && (
        <div className="space-y-2" data-testid="reference-suggestions">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-amber-ink">Found in your records</p>
            {found.length > 1 && (
              <Button size="sm" variant="secondary" disabled={editing.saving} onClick={() => editing.confirmSuggestions(found.map((r) => r.id))} data-testid="confirm-all-references">Add all {found.length}</Button>
            )}
          </div>
          {found.map((r) => (
            <SuggestionRow key={r.id} disabled={editing.saving} title={<span className="pd-mono text-ink">{r.value}</span>}
              actions={[{ label: 'Add', onClick: () => editing.confirmSuggestions([r.id]), testId: `confirm-suggestion-${r.id}` }]}
              onIgnore={() => editing.rejectSuggestions([r.id])} />
          ))}
        </div>
      )}
    </section>
  );
};

// Approximate dates of a school year, from the date of birth (Sep – Jul).
const yearDates = (dob, number) => {
  const value = UK_YEAR_GROUPS.find((g) => g.index === number)?.value;
  const d = dob && value ? yearGroupsToDates(dob, value, value) : null;
  return d?.start_date ? `${formatMonthYear(d.start_date)} – ${formatMonthYear(d.end_date)}` : null;
};

const TILE_COLS = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' };

/**
 * "Your time here" — the organisation's first tab: what this place was in
 * the person's life, not what's in its file. How long, the homes (for a
 * council or agency) or school years (for a school), the people, the
 * numbers they used, and the summary — with the file itself, the requests
 * and the checks one click away on their own tabs. Everything edits in
 * place (useOrgEditing); the profile adds what the records say once it's
 * open (POST /organisations/{id}/profile).
 */
export const OrgTimeHere = ({
  org, cabinet, profile, profileState, editing, findings, newFindings = 0, gapStatus, loadingFindings,
  review = { state: 'idle' }, unanalysedCount = 0, onOpenTab, onOpenNoticed, onOpenRecord, onUpload, onAnalyse,
}) => {
  const navigate = useNavigate();
  const [addHome, setAddHome] = useState(false);
  const [addPerson, setAddPerson] = useState(false);
  const placing = ORG_TYPES_WITH_PLACEMENTS.includes(org.type) || (org.placements || []).length > 0;

  const groups = useMemo(() => buildPeople({
    org, profile, respitePool: editing.respitePool, siblings: editing.siblings, showSiblings: SIBLING_ORG_TYPES.has(org.type),
  }), [org, profile, editing.respitePool, editing.siblings]);
  const schoolYears = org.type === 'school' ? schoolYearsFor(org, profile) : [];
  // A school given as years only still lines up with the homes, via the date of birth.
  const timeOrg = useMemo(() => {
    if (org.start_date || org.type !== 'school' || !editing.dob) return org;
    const d = yearGroupsToDates(editing.dob, org.year_group_start, org.year_group_end);
    return d.start_date ? { ...org, ...d } : org;
  }, [org, editing.dob]);
  const during = placing ? [] : homesDuring(timeOrg, editing.allOrgs);
  const tiles = glanceTiles({ org, groups, profile, homesWhileHere: during, yearGroups: schoolYears });
  const totals = cabinet?.totals || {};
  const hasRecords = (totals.records || 0) > 0;
  const placers = [...new Set(during.map((d) => d.org.name))];
  const newOrgs = (org.pending_suggestions || []).filter((s) => s.type === 'new_organisation');

  return (
    <div className="space-y-10" data-testid="org-time-here">
      {org.unresolved && <ResolveCouncil org={org} editing={editing} />}
      <DatesFound org={org} editing={editing} />

      {tiles.length > 0 && (
        <section aria-label="At a glance" className={`grid grid-cols-2 ${TILE_COLS[tiles.length] || 'lg:grid-cols-4'} gap-3`}>
          {tiles.map((t) => <Tile key={t.id} label={t.label} value={t.value} hint={t.hint} testId={`org-tile-${t.id}`} />)}
        </section>
      )}

      {placing && <OrgHomes org={org} profile={profile} editing={editing} onAddHome={() => setAddHome(true)} />}

      {org.type === 'school' && schoolYears.length > 0 && (
        <section aria-labelledby="org-years-heading" className="space-y-3" data-testid="org-school-years">
          <h2 id="org-years-heading" className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">Your school years</h2>
          <ol className="flex flex-wrap gap-3">
            {schoolYears.map((y) => {
              const when = yearDates(editing.dob, y.number);
              return (
                <li key={y.label} className="flex-[1_1_160px] min-w-0 rounded-lg bg-ground p-4 flex flex-col gap-1" data-testid={`school-year-${y.number}`}>
                  <span className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">{y.label}</span>
                  {when && <span className="pd-mono">{when}</span>}
                  {y.forms.length > 0 && <span className="text-sm text-ink-70">Form <span className="pd-mono text-ink">{y.forms.join(' · ')}</span></span>}
                  {(y.classes > 0 || y.records > 0) && (
                    <span className="text-sm text-ink-55">{[y.classes ? `${y.classes} class${y.classes === 1 ? '' : 'es'}` : null, y.records ? `${y.records} record${y.records === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {!placing && during.length > 0 && (
        <section aria-labelledby="org-during-heading" className="space-y-3" data-testid="org-homes-during">
          <h2 id="org-during-heading" className={org.type === 'school' && schoolYears.length ? 'sr-only' : 'font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink'}>Where you were living at the time</h2>
          <HomesStrip placements={during.map((d) => d.placement)} label={org.type === 'school' && schoolYears.length ? `Where you were living at the time · from ${placers.join(' and ')}` : `From ${placers.join(' and ')}`} />
          <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-70">
            {during.map((d) => (
              <li key={`${d.org.id}-${d.placement.id}`}>
                <Link to={d.placement.linked_org_id ? `/organisations/${d.placement.linked_org_id}` : `/organisations/${d.org.id}/placements/${d.placement.id}`} className="font-semibold text-brand hover:text-brand-ink">{homeName(d.placement)}</Link>
                <span className="text-ink-55"> · {formatMonthYear(d.placement.start_date)} – {formatMonthYear(d.placement.end_date)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <OrgPeople org={org} groups={groups} editing={editing} onAddPerson={() => setAddPerson(true)} profileState={profileState} />

      <Numbers org={org} profile={profile} editing={editing} onChange={() => navigate(`/organisations?edit=${org.id}`)} />

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 min-w-0">
          {hasRecords ? (
            <OrgSummary orgId={org.id} orgName={org.name} onOpenRecord={onOpenRecord} ready={analysisSettled(org.documents)} />
          ) : (
            <div className="rounded-lg bg-ground p-6 space-y-2" data-testid={SAR_IN_PROGRESS.has(org.sar_status) ? 'org-waiting-on-them' : 'org-nothing-here-yet'}>
              <p className="pd-eyebrow">Your time with them, in brief</p>
              <p className="text-base text-ink-70 max-w-[60ch]">
                {unanalysedCount > 0
                  ? `Once ${unanalysedCount === 1 ? 'the document' : 'the documents'} from ${org.name} ${unanalysedCount === 1 ? 'has' : 'have'} been analysed, a short summary of your time with them appears here.`
                  : SAR_IN_PROGRESS.has(org.sar_status)
                    ? `You've asked ${org.name} for your records. A short summary of your time with them appears here once they arrive.`
                    : `A short summary of your time with them appears here once you have records from ${org.name}.`}
              </p>
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="rounded-lg bg-ground p-5 space-y-2.5" data-testid="org-gaps-card">
            <p className="pd-eyebrow">What we noticed</p>
            {loadingFindings ? (
              <p className="text-sm text-ink-55 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Checking what should be here…</p>
            ) : findings?.length ? (
              <>
                <p className="text-[15px] text-ink-70" data-testid="org-noticed-summary">{findings.length} thing{findings.length === 1 ? '' : 's'} worth a look in what {org.name} holds. These are pointers, not answers.</p>
                <ul className="flex flex-wrap gap-2">
                  {FINDING_CATEGORIES.map((c) => ({ ...c, n: findings.filter((g) => categoryOf(g) === c.id).length })).filter((c) => c.n).map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => onOpenNoticed(c.id)} data-testid={`org-noticed-open-${c.id}`}
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium border bg-surface text-ink-70 border-rule hover:bg-inset transition-colors duration-state">
                        {c.label} <span className="pd-mono">{c.n}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {newFindings > 0 && (
                  <p className="inline-flex items-center gap-1.5 text-sm text-brand-ink" data-testid="org-noticed-new">
                    <span aria-hidden="true" className="w-[8px] h-[8px] rounded-full bg-brand" />{newFindings} new since you last looked
                  </p>
                )}
                <div><Button variant="secondary" size="sm" className="bg-surface hover:bg-inset" onClick={() => onOpenNoticed(null)} data-testid="org-noticed-open">Look through them</Button></div>
              </>
            ) : (
              <p className="text-[15px] text-ink-70" data-testid="org-gaps-empty">{noticedEmptyMessage(org, gapStatus, hasRecords)}</p>
            )}
            {(review.state === 'auto' || review.state === 'running') && (
              <p className="text-sm text-ink-55 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden="true" /> An AI is reading the whole file; this will be brought up to date when it&apos;s done.</p>
            )}
          </div>
          <div className="rounded-lg bg-ground p-5 space-y-2.5" data-testid="org-file-card">
            <p className="pd-eyebrow">Their file</p>
            <p className="text-[15px] text-ink-70">
              {hasRecords
                ? `${totals.records} record${totals.records === 1 ? '' : 's'} from ${totals.documents} document${totals.documents === 1 ? '' : 's'}${profile?.first_date ? `, dated ${profile.first_date.slice(0, 4)}${profile.last_date && profile.last_date.slice(0, 4) !== profile.first_date.slice(0, 4) ? ` to ${profile.last_date.slice(0, 4)}` : ''}` : ''}.`
                : (org.documents || []).length ? 'Nothing has been read from their documents yet.' : `Nothing from ${org.name} yet.`}
            </p>
            {unanalysedCount > 0 && (
              <div className="rounded-md bg-amber-wash px-3 py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid="org-analyse-prompt">
                <p className="text-sm text-amber-ink">{unanalysedCount} document{unanalysedCount === 1 ? '' : 's'} waiting to be analysed.</p>
                <Button size="sm" variant="secondary" className="bg-surface hover:bg-inset" onClick={onAnalyse} data-testid="org-analyse-now">Analyse {unanalysedCount === 1 ? 'it' : 'them'}</Button>
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {hasRecords && <button type="button" onClick={() => onOpenTab('records')} className="text-sm font-semibold text-brand hover:text-brand-ink">Browse the records</button>}
              {!hasRecords && <button type="button" onClick={onUpload} className="text-sm font-semibold text-brand hover:text-brand-ink" data-testid="org-upload-now">Upload a document</button>}
              <button type="button" onClick={() => onOpenTab('data')} className="text-sm font-semibold text-brand hover:text-brand-ink">Requests and data</button>
            </div>
          </div>
        </div>
      </div>

      {newOrgs.length > 0 && (
        <section aria-labelledby="org-elsewhere-heading" className="space-y-3" data-testid="found-org-suggestions-card">
          <h2 id="org-elsewhere-heading" className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">Other places these records name</h2>
          <p className="text-[15px] text-ink-55 max-w-[65ch]">Not in your account yet. Each holds its own records about you, so you could ask them too.</p>
          <FoundBox>
            {newOrgs.map((s) => (
              <SuggestionRow
                key={s.id} testId={`new-org-suggestion-${s.id}`} disabled={editing.saving} title={s.name}
                detail={[getOrgTypeLabel(s.suggested_type), (s.people || []).length ? `with ${s.people.map((p) => p.name).join(', ')}` : null].filter(Boolean).join(' · ')}
                actions={[{ label: 'Add', onClick: () => editing.confirmSuggestions([s.id]), testId: `confirm-suggestion-${s.id}` }]}
                onIgnore={() => editing.rejectSuggestions([s.id])}
              />
            ))}
          </FoundBox>
        </section>
      )}

      <AddHomeDialog open={addHome} onOpenChange={setAddHome} orgName={org.name} respitePool={editing.respitePool}
        onPoolChange={editing.savePool} onAdd={editing.addPlacement} saving={editing.saving} />
      <AddPersonDialog open={addPerson} onOpenChange={setAddPerson} org={org} onAdd={editing.addPerson} saving={editing.saving} />
    </div>
  );
};

export default OrgTimeHere;
